import { Box3, ShapeGeometry, Vector3 } from "three";
import { SVGLoader } from "three-stdlib"; // SVGLoader is a class
import { MM_TO_INTERNAL_FACTOR } from "./constants";

// Define a 2D point type
type Point = { x: number; y: number };

/**
 * Calculates the convex hull (outer boundary) of SVG paths to generate a clean outer outline.
 *
 * @param {ReturnType<typeof SVGLoader.prototype.parse>["paths"]} paths - The SVG paths to process.
 * @returns {[number, number, number][]} - Array of 3D points forming the outer outline.
 */
export const calculateOuterOutline = (
  paths: ReturnType<typeof SVGLoader.prototype.parse>["paths"]
): [number, number, number][] => {
  // Collect all points from the SVG paths
  const allPoints: Point[] = paths.flatMap((path) =>
    path.subPaths.flatMap((subPath) => subPath.getPoints(100))
  );

  // Convex Hull algorithm
  const convexHull = (points: Point[]): Point[] => {
    if (points.length <= 3) return points;

    // Sort points by x-coordinate, then y-coordinate
    points.sort((a, b) => a.x - b.x || a.y - b.y);

    const cross = (o: Point, a: Point, b: Point) =>
      (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

    const lower: Point[] = [];
    for (const p of points) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
        lower.pop();
      }
      lower.push(p);
    }

    const upper: Point[] = [];
    for (let i = points.length - 1; i >= 0; i--) {
      const p = points[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
        upper.pop();
      }
      upper.push(p);
    }

    lower.pop();
    upper.pop();

    return lower.concat(upper);
  };

  // Compute the convex hull
  const outerPoints = convexHull(allPoints).map((p) => [p.x, p.y, 0] as [number, number, number]);

  // Close the outline by connecting the last point to the first
  if (outerPoints.length > 0) {
    outerPoints.push(outerPoints[0]);
  }

  return outerPoints;
};

/**
 * Parses CSS text and converts it into an object mapping class selectors to their style properties.
 *
 * @param {string} cssText - The raw CSS text to parse.
 * @returns {Record<string, Record<string, string>>} - An object where keys are class selectors (e.g., '.cls-1')
 * and values are objects of style properties (e.g., { fill: '#fff', stroke: '#000' }).
 */
export const parseCss = (
  cssText: string
): Record<string, Record<string, string>> => {
  const styles: Record<string, Record<string, string>> = {};

  // Use the browser's DOMParser to parse the CSS text into rules
  const styleSheet = new CSSStyleSheet();
  try {
    styleSheet.replaceSync(cssText);
  } catch (err) {
    console.error("Error parsing CSS:", err);
    return styles;
  }

  // Convert CSSRuleList to an array for iteration
  const rules = Array.from(styleSheet.cssRules);

  // Iterate over each rule in the stylesheet
  for (const rule of rules) {
    if (rule.type === CSSRule.STYLE_RULE) {
      const styleRule = rule as CSSStyleRule;
      const selectors = styleRule.selectorText.split(","); // Handle multiple selectors
      const style = styleRule.style;

      // Extract CSS properties and convert RGB to Hex
      const styleObj: Record<string, string> = {};
      for (const prop of Array.from(style)) {
        let value = style.getPropertyValue(prop).trim();
        if (value.startsWith("rgb")) {
          value = rgbToHex(value); // Convert RGB to Hex
        }
        styleObj[prop] = value;
      }

      // Assign the style object to each selector
      selectors.forEach((selector) => {
        styles[selector.trim()] = {
          ...styles[selector.trim()],
          ...styleObj,
        };
      });
    }
  }

  return styles;
};

/**
 * Converts an RGB string to Hex.
 *
 * @param {string} rgb - The RGB string (e.g., "rgb(29, 29, 27)").
 * @returns {string} - The Hex string (e.g., "#1d1d1b").
 */
const rgbToHex = (rgb: string): string => {
  const rgbValues = rgb
    .match(/\d+/g)
    ?.map((val) => parseInt(val).toString(16).padStart(2, "0"));
  return rgbValues ? `#${rgbValues.join("")}` : rgb;
};

/**
 * Fetches the SVG file from the specified path and extracts the CSS styles from the <style> tag.
 *
 * @param {string} svgPath - The path or URL of the SVG file.
 * @returns {Promise<Record<string, Record<string, string>>>} - A promise that resolves to an object of parsed CSS styles.
 */
export const fetchSvgStyles = async (
  svgPath: string
): Promise<Record<string, Record<string, string>>> => {
  const response = await fetch(svgPath);
  const svgText = await response.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, "image/svg+xml");
  const styleTag = doc.querySelector("style");

  if (styleTag) {
    return parseCss(styleTag.textContent || "");
  }
  return {};
};

export const getStyle = (
  styles: Record<string, Record<string, string>>,
  className: string,
  property: string
): string | null => 
   className
    .split(" ") // Handle multiple classes in the same element
    .map((cls) => styles[`.${cls}`]?.[property]) // Resolve property for each class
    .find((value) => value && value !== "none") || null // Return the first valid value
;


/**
 * Calculates the scale factors and the unit-based bounding box for SVG paths to fit a given width and height.
 *
 * This function performs the following steps:
 * 1. Converts SVG paths into 3D shapes using `SVGLoader.createShapes`.
 * 2. Computes the original bounding box of the generated shapes in pixel units.
 * 3. Calculates the scale factors (`scaleX` and `scaleY`) to fit the shapes into the given width and height in millimeters.
 * 4. Scales the bounding box to reflect its size in scene units.
 *
 * @param {ReturnType<typeof SVGLoader.prototype.parse>["paths"]} paths - Array of SVG paths to convert into 3D geometry.
 * @param {number} width - Desired width of the product in millimeters.
 * @param {number} height - Desired height of the product in millimeters.
 * @returns {{ scaleX: number, scaleY: number, boundingBox: Box3 }} - An object containing:
 *    - `scaleX`: Scaling factor along the X-axis.
 *    - `scaleY`: Scaling factor along the Y-axis.
 *    - `boundingBox`: A `Box3` representing the scaled bounding box in scene units.
 */
 export const calculateScaleAndBoundingBox = (
  paths: ReturnType<typeof SVGLoader.prototype.parse>["paths"],
  width: number,
  height: number
): { scaleX: number; scaleY: number; boundingBox: Box3 } => {
  let scaleX = 1, scaleY = 1;
  const boundingBox = new Box3(); 
  const boundingBoxSize = new Vector3(1, 1, 1); 

  if (paths.length > 0) {
    const shapes = paths.flatMap((path) => SVGLoader.createShapes(path));
    const geometry = new ShapeGeometry(shapes);

    // compute the bounding box in the original SVG coordinate system (pixels)
    geometry.computeBoundingBox();

    if (geometry.boundingBox) {
      geometry.boundingBox.getSize(boundingBoxSize);
      boundingBox.copy(geometry.boundingBox);

      const viewBoxWidth = boundingBoxSize.x;
      const viewBoxHeight = boundingBoxSize.y;

      if (viewBoxWidth && viewBoxHeight) {
        // convert desired dimensions from millimeters to internal scene units
        const widthInUnits = width * MM_TO_INTERNAL_FACTOR;
        const heightInUnits = height * MM_TO_INTERNAL_FACTOR;

        // calculate the scale factors to match the desired size
        scaleX = widthInUnits / viewBoxWidth;
        scaleY = heightInUnits / viewBoxHeight;

        // scale the bounding box to match the scene's unit system
        boundingBox.min.multiplyScalar(scaleX);
        boundingBox.max.multiplyScalar(scaleY);
      }
    }
  }

  return { scaleX, scaleY, boundingBox };
};

/**
 * Fetches the SVG file from the specified path and extracts the fill attribute from the root <svg> element.
 *
 * @param {string} svgPath - The path or URL of the SVG file.
 * @returns {Promise<string | null>} - A promise that resolves to the fill attribute value or null if not found or if it's "none".
 */
 export const fetchSvgFill = async (svgPath: string): Promise<string | null> => {
  try {
    const response = await fetch(svgPath);
    const svgText = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");
    const fillAttr = doc.documentElement.getAttribute("fill");
    return fillAttr && fillAttr !== "none" ? fillAttr : null;
  } catch (error) {
    console.error("Error fetching SVG fill:", error);
    return null;
  }
};
